import * as S from "effect-app/Schema"
import type { Brand } from "effect/Brand"

export interface VatRateBrand extends Brand<"VatRate"> {}
export type VatRate = number & VatRateBrand

/**
 * VAT rate in percent (19, 20, 5.5). Decimals allowed, inclusive 0..100.
 * Same construction as effect-app's NonNegativeNumber: the `isBetween` check
 * gives OmegaForm its min/max metadata and localized messages, the nominal
 * brand + withDefaultMake make `VatRate(19)` a validating constructor.
 * Whether VAT applies at all is decided by the shop type (Consumer = gross prices).
 */
export const VatRate = S.Finite.pipe(
  S.check(S.isBetween({ minimum: 0, maximum: 100 })),
  S.fromBrand(S.nominal<VatRate>(), { identifier: "VatRate", jsonSchema: {} }),
  S.withDefaultMake
)

/** Historic German default; also the migration value for legacy shops. */
export const DEFAULT_VAT_RATE = VatRate(19)

/**
 * Creates a function that multiplies a price by a given multiplier and optionally rounds the result.
 * Used for applying price multipliers like VAT or the Swiss market multiplier.
 * The multiplier is scaled by 100 to handle decimal multipliers with precision.
 */
export const makeMultiply = (multiplier: number, rounder: (price: number) => number = (n) => n) => (value: number) =>
  rounder((value * (multiplier * 100)) / 100)

/**
 * net -> gross. Same formula the mapper used with the hardcoded 1.19, so
 * existing prices are bit-identical at rate 19. Half-cent ties inherit the
 * rounder's float behaviour (see docs/superpowers/specs/2026-09-09-shop-vat-rate-design.md, "Price math").
 */
export const addVat = (rate: number, rounder: (n: number) => number) => makeMultiply(1 + rate / 100, rounder)

/** gross -> net. Same formula as the old Shop.netPrice; deliberately unrounded. */
export const removeVat = (rate: number) => (gross: number) => gross / (1 + rate / 100)
